import { Drawer, Button, Typography, Box, IconButton, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { OperationForm } from './OperationForm';
import { useEffect, useMemo, useState } from 'react';
import { PatchType } from '../../../types';
import { getVisibleOperationTypes, formatOperationTypeName } from '../constants/operationTypes';
// import { useDispatch } from 'react-redux';

import { useMenuDataCache } from '../hooks/useMenuDataCache';
import { useOperationForm } from '../hooks/useOperationForm';
import { useFeatureFlag } from '../../../services/optimizely/optimizelyHooks';
import FeatureFlags from '../../../constants/FeatureFlags';
import type { PatchOperation, Patch, Store } from '../types/patchTypes';



interface OperationDrawerProps {
  mode?: 'create' | 'edit' | 'remove';
  initialEdit?: PatchOperation | null;
  onClose?: () => void;
  patchType?: PatchType;
  availablePatches?: Patch[];
  selectedStores?: Store[];
  getPatchIdsForType?: (patchType: PatchType) => string[];
  addPendingOp?: (opObj: { minimal: PatchOperation; enriched: PatchOperation }, type?: 'set' | 'remove') => void;
}

export function OperationDrawer({ mode = 'create', initialEdit = null, onClose, patchType = PatchType.PRODUCT_PRICE, availablePatches = [], selectedStores = [], getPatchIdsForType, addPendingOp }: OperationDrawerProps) {
  // Determine mode flags early
  const isEdit = mode === 'edit';
  const isRemove = mode === 'remove';

  // Get all operation types allowed for the current patch type using shared constant
  const { isEnabled: namedPricingEnabled } = useFeatureFlag(FeatureFlags.NAMED_PRICING_ENABLED);
  const { isEnabled: rangedPricingEnabled, flagReady: rangedFlagReady } = useFeatureFlag(
    FeatureFlags.RANGED_PRICING_ENABLED
  );
  // Ranged pricing is on by default, and useFeatureFlag reports false for an undecided flag, so keep
  // it visible until Optimizely has actually answered.
  const uniqueOperationTypes = useMemo(
    () =>
      getVisibleOperationTypes(patchType, {
        namedPricingEnabled,
        rangedPricingEnabled: rangedPricingEnabled || !rangedFlagReady,
        allowRetiredForRemoval: isRemove,
      }),
    [patchType, namedPricingEnabled, rangedPricingEnabled, rangedFlagReady, isRemove]
  );
  // console.log('[OperationDrawer] patchType:', patchType, 'uniqueOperationTypes:', uniqueOperationTypes, 'availablePatches:', availablePatches);

  // Operation type selection
  const [operationType, setOperationType] = useState<string>(uniqueOperationTypes[0] || '');

  // Menu data cache
  const { menu, fetchMenuData } = useMenuDataCache();

  // Operation form state

  const {
    fields,
    handlers,
    isValid
  } = useOperationForm({
    mode,
    operationTypes: uniqueOperationTypes,
    availablePatches,
    addPendingOp,
    menu: menu || { products: [], bundles: [] }, // Provide fallback for null menu
  });

  // On mount, fetch menu data (will only load once due to cache logic)
  useEffect(() => {
    fetchMenuData();
  }, [fetchMenuData]);

  // When patch type changes, update operation type to first available
  useEffect(() => {
    if (uniqueOperationTypes.length > 0) {
      setOperationType(uniqueOperationTypes[0]);
      handlers.setOperationType(uniqueOperationTypes[0]);
    }
  }, [patchType]);

  // If a flag resolves after mount and hides the selected operation type, fall back to a visible one
  useEffect(() => {
    if (uniqueOperationTypes.length > 0 && operationType && !uniqueOperationTypes.includes(operationType)) {
      setOperationType(uniqueOperationTypes[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqueOperationTypes]);

  // When operation type changes, update form state
  useEffect(() => {
    if (operationType) {
      handlers.setOperationType(operationType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operationType]);

  const [open, setOpen] = useState(true);
  const handleClose = () => {
    setOpen(false);
    if (onClose) onClose();
  };

  const canSave = isValid();

  const handleSave = () => {
    // Belt as well as braces: the button below is disabled when this is false, but a save that got
    // here anyway would build operations from an incomplete form and queue them silently.
    if (!canSave) return;
    handlers.save();
    handleClose();
  };

  return (
    <>
      {isEdit && (
        <Button
          variant="contained"
          onClick={() => setOpen(true)}
          color="primary"
        >
          Edit Operation
        </Button>
      )}

      <Drawer anchor="right" open={open || isEdit} onClose={handleClose}>
        <Box p={2} width={600} data-testid="operationWindow">
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">
              {isEdit ? 'Edit Operation' : isRemove ? 'Remove Operation' : 'Add Operation'}
            </Typography>
            <IconButton 
              onClick={handleClose} 
              data-testid="closeOperationDrawerButton"
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Patch selection dropdown removed: operations are based on patch type only */}

          {/* Operation type select */}
          {uniqueOperationTypes.length > 0 && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="operation-type-select-label">Operation Type</InputLabel>
              <Select
                labelId="operation-type-select-label"
                data-testid="operation-type-select"
                value={operationType}
                label="Operation Type"
                onChange={(e) => setOperationType(e.target.value)}
              >
                {uniqueOperationTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {formatOperationTypeName(type)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Operation form for all modes */}
          {operationType && (
            <Box mt={2}>
              <OperationForm
                fields={fields}
                handlers={handlers}
                mode={mode}
              />
            </Box>
          )}

          {/* Action buttons */}
          <Box mt={3} display="flex" justifyContent="flex-end">
            <Button
              variant="outlined"
              data-testid="cancelOperationButton"
              onClick={handleClose}
              sx={{ mr: 1 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              data-testid="saveOperationButton"
              onClick={handleSave}
              disabled={!canSave}
              color={isRemove ? "error" : "primary"}
            >
              {isEdit ? 'Update' : isRemove ? 'Remove' : 'Save'}
            </Button>
          </Box>
        </Box>
      </Drawer>
    </>
  );
}
